import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import SelectField from "../common/SelectField.js"
import { mlSheetService } from "../../services/mlSheetService"
import { shipmentService } from "../../services/shipmentService"
import { paymentService } from "../../services/ShipmentPaymentService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from "../common/page_component/index.js"
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import Button from "../common/Button.js"
import ListPagination from "../common/ListPagination.js"
import SearchSubmitInput from "../common/SearchSubmitInput.js"
import { mergeLoadedPage } from "../../utils/mergeLoadedPage.js"
import PaymentQrModal from "../common/PaymentQrModal.js"
import DeliveryOtpModal from "../common/DeliveryOtpModal.js"
import { Package, RefreshCw, Undo2 } from "lucide-react"
import { getBulkStatusOptions, isShipmentFullyPaid } from "../../utils/shipmentPayment.js"
import { canRevertDelivery, revertDeliveryDisabledReason } from "../../utils/deliveryRevert.js"
import { isOtpRequiredResponse, normalizeOtpPending } from "../../utils/deliveryOtp.js"
import DeliveryProofCapture from "./DeliveryProofCapture.js"
import shipmentCrossingService from "../../services/shipmentCrossingService.js"
import { HubShortfallNote, openShortfall } from "./HubShortfallNote.js"
import usePaginatedListFetch from "../../utils/usePaginatedListFetch.js"
import { useTranslation } from "react-i18next"

const isStatusUpdateLocked = (status) => String(status || "").toLowerCase() === "delivered"
const FILTER_OPTIONS = ["Dispatched", "Received", "Delivered"]
const PER_PAGE = 10
const MISSING_NOTE_OPTIONS = [
  "Parcel in open condition",
  "Damaged parcel",
  "Parcel shortage / missing items",
]

const AgentUpdateStatus = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [selectedById, setSelectedById] = useState({})
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("Dispatched")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [allowDelivery, setAllowDelivery] = useState(
    user?.location_hub_allow_delivery !== false,
  )
  const [remitLockedSheetCount, setRemitLockedSheetCount] = useState(0)

  const [selectedStatus, setSelectedStatus] = useState("Received")
  const [remarks, setRemarks] = useState("Status updated via Update Status")
  const [missingNote, setMissingNote] = useState("")

  const [deliveryReceiver, setDeliveryReceiver] = useState("")
  const [deliveryReceiverContact, setDeliveryReceiverContact] = useState("")
  const [deliveryReceiverImage, setDeliveryReceiverImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [deliveryPhoto, setDeliveryPhoto] = useState(null)
  const [deliveryPhotoPreview, setDeliveryPhotoPreview] = useState(null)
  const [agentUnloadingAmount, setAgentUnloadingAmount] = useState("")
  const [deliveryVehicleNo, setDeliveryVehicleNo] = useState("")
  const [lastMileHours, setLastMileHours] = useState("2")

  const [paymentMode, setPaymentMode] = useState("upi")
  const [paymentAmount, setPaymentAmount] = useState("")
  const [showPaymentQr, setShowPaymentQr] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState(null)
  const [pendingDeliverAfterPay, setPendingDeliverAfterPay] = useState(null)
  const [otpPending, setOtpPending] = useState([])
  const [revertingId, setRevertingId] = useState("")
  const requestSeqRef = useRef(0)
  const resolvedSearchRef = useRef(null)

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  )

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const statusFilterKey = statusFilter

  const applyShipmentPage = (response, page = currentPage) => {
    const list = response.data?.shipments || []
    const pagination = response.data?.pagination || {}
    const pages = Math.max(1, pagination.pages || 1)
    const total = pagination.total ?? response.data?.total ?? 0
    const rows = Array.isArray(list) ? list : []
    setShipments((prev) => mergeLoadedPage(page, rows, prev))
    setTotalPages(pages)
    setTotalCount(total)
    setRemitLockedSheetCount(Number(response.data?.remit_locked_sheet_count || 0))
    if (typeof response.data?.location_hub_allow_delivery === "boolean") {
      setAllowDelivery(response.data.location_hub_allow_delivery)
    } else if (user?.location_hub_allow_delivery !== undefined) {
      setAllowDelivery(user.location_hub_allow_delivery !== false)
    }
    return { list, pages }
  }

  const fetchData = useCallback(async () => {
    const seq = ++requestSeqRef.current
    const trimmedSearch = searchQuery.trim()
    try {
      setLoading(true)
      const response = await mlSheetService.getAgentStatusShipments({
        page: currentPage,
        perPage: PER_PAGE,
        search: searchQuery,
        statuses: statusFilter && statusFilter !== "Missing" ? [statusFilter] : [],
        missing: statusFilter === "Missing",
      })
      if (seq !== requestSeqRef.current) return

      const focusStatus = response.data?.search_focus_status
      const unseenSearch = resolvedSearchRef.current !== trimmedSearch
      if (
        statusFilter !== "Missing" &&
        unseenSearch &&
        focusStatus &&
        FILTER_OPTIONS.includes(focusStatus) &&
        focusStatus !== statusFilter
      ) {
        // Open the tab this shipment is actually on, then show that tab's rows.
        // A later manual tab click keeps the search but does not jump back.
        const focusedResponse = await mlSheetService.getAgentStatusShipments({
          page: 1,
          perPage: PER_PAGE,
          search: searchQuery,
          statuses: [focusStatus],
        })
        if (seq !== requestSeqRef.current) return
        resolvedSearchRef.current = trimmedSearch
        setStatusFilter(focusStatus)
        if (currentPage !== 1) setCurrentPage(1)
        const { list, pages } = applyShipmentPage(focusedResponse, 1)
        if (list.length === 0 && pages > 1) setCurrentPage(pages)
        return
      }
      if (unseenSearch) resolvedSearchRef.current = trimmedSearch

      const { list, pages } = applyShipmentPage(response)
      if (list.length === 0 && currentPage > pages) {
        setCurrentPage(pages)
      }
    } catch (error) {
      if (seq !== requestSeqRef.current) return
      console.error("Error fetching assigned shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to load assigned shipments")
    } finally {
      if (seq === requestSeqRef.current) setLoading(false)
    }
  }, [user?.location_hub_allow_delivery, currentPage, searchQuery, statusFilter])

  usePaginatedListFetch({
    fetchPage: fetchData,
    setCurrentPage,
    currentPage,
    resetPageDeps: [searchQuery, statusFilterKey],
    debounceWhen: searchQuery,
    debounceMs: 0,
  })

  const filteredShipments = shipments

  const selectableFilteredShipments = useMemo(
    () => filteredShipments.filter((s) => !isStatusUpdateLocked(s.status)),
    [filteredShipments],
  )

  const selectedShipments = useMemo(
    () => new Set(Object.keys(selectedById)),
    [selectedById],
  )

  const selectedList = useMemo(
    () => Object.values(selectedById).filter((s) => s && !isStatusUpdateLocked(s.status)),
    [selectedById],
  )

  const unpaidSelected = useMemo(
    () => selectedList.filter((s) => !isShipmentFullyPaid(s)),
    [selectedList],
  )

  const needsDeliveryPayment =
    selectedStatus === "Delivered" && unpaidSelected.length > 0

  const bulkStatusConfig = useMemo(
    () => getBulkStatusOptions(new Set(selectedList.map((s) => s.shipment_id)), selectedList),
    [selectedList],
  )

  const deliverableSelected = selectedList.filter(
    (shipment) => !shipment.delivery_locked_until_settlement,
  )
  const lockedSelected = selectedList.filter(
    (shipment) => shipment.delivery_locked_until_settlement,
  )
  const settlementBlocksDelivery =
    selectedList.length > 0 && deliverableSelected.length === 0 && lockedSelected.length > 0
  const canDeliver =
    (user?.role !== "Agent" || allowDelivery) && !settlementBlocksDelivery

  const statusOptions = useMemo(() => {
    const base =
      bulkStatusConfig.options.length > 0
        ? bulkStatusConfig.options
        : canDeliver
          ? ["Received", "Delivered"]
          : ["Received"]
    let options = base.map((status) => ({ value: status, label: status }))
    if (!canDeliver) {
      options = options.filter((o) => o.value !== "Delivered")
    }
    return options
  }, [bulkStatusConfig.options, canDeliver])

  useEffect(() => {
    const auto = bulkStatusConfig.autoSelect
    if (auto && (auto !== "Delivered" || canDeliver)) {
      setSelectedStatus(auto)
      return
    }
    setSelectedStatus((prev) => {
      const values = statusOptions.map((o) => o.value)
      if (values.includes(prev)) return prev
      return values[0] || "Received"
    })
  }, [bulkStatusConfig, statusOptions, canDeliver])

  useEffect(() => {
    setSelectedById((prev) => {
      const keys = Object.keys(prev)
      if (keys.length === 0) return prev
      let changed = false
      const next = { ...prev }
      for (const shipment of shipments) {
        if (!next[shipment.shipment_id]) continue
        if (isStatusUpdateLocked(shipment.status)) {
          delete next[shipment.shipment_id]
          changed = true
          continue
        }
        const current = next[shipment.shipment_id]
        if (
          current.status !== shipment.status ||
          current.balance_amount !== shipment.balance_amount ||
          current.mode_of_payment !== shipment.mode_of_payment
        ) {
          next[shipment.shipment_id] = shipment
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [shipments])

  useEffect(() => {
    setSelectedById({})
  }, [statusFilterKey])

  useEffect(() => {
    if (needsDeliveryPayment && unpaidSelected.length === 1) {
      setPaymentAmount(String(parseFloat(unpaidSelected[0].balance_amount || 0).toFixed(2)))
    } else if (!needsDeliveryPayment) {
      setPaymentAmount("")
    }
  }, [needsDeliveryPayment, unpaidSelected])

  const allFilteredSelected =
    selectableFilteredShipments.length > 0 &&
    selectableFilteredShipments.every((s) => selectedShipments.has(s.shipment_id))

  const selectStatusFilter = (status) => {
    setStatusFilter(status)
  }

  const packageLines = (shipment) => {
    const lines = Array.isArray(shipment?.package_lines) ? shipment.package_lines : []
    const named = lines.filter((line) => line && (line.name || line.qty != null))
    if (named.length) return named
    if (shipment?.article_type || shipment?.no_of_articles != null) {
      return [{ name: shipment.article_type, qty: shipment.no_of_articles }]
    }
    return []
  }

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    setSelectedById((prev) => {
      const next = { ...prev }
      if (checked) {
        selectableFilteredShipments.forEach((s) => {
          next[s.shipment_id] = s
        })
      } else {
        selectableFilteredShipments.forEach((s) => {
          delete next[s.shipment_id]
        })
      }
      return next
    })
  }

  const handleSelectShipment = (shipmentId, status, shipment) => {
    if (isStatusUpdateLocked(status)) return
    setSelectedById((prev) => {
      if (prev[shipmentId]) {
        const next = { ...prev }
        delete next[shipmentId]
        return next
      }
      return { ...prev, [shipmentId]: shipment }
    })
  }

  const handleMarkMissing = async () => {
    const ids = selectedList
      .filter((s) => String(s.status) === "Dispatched" && !openShortfall(s))
      .map((s) => s.shipment_id)
    if (ids.length === 0) {
      Alert.error("Select Dispatched shipments that are not already missing")
      return
    }
    if (!missingNote) {
      Alert.error("Select a missing reason")
      return
    }
    try {
      setUpdating(true)
      const response = await shipmentCrossingService.markMissing({
        shipmentIds: ids,
        note: missingNote,
        source: "destination",
      })
      Alert.success(response.message || "Marked missing")
      setSelectedById({})
      setMissingNote("")
      await fetchData()
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to mark missing")
    } finally {
      setUpdating(false)
    }
  }

  const handleRevertDelivery = async (shipment) => {
    const blocked = revertDeliveryDisabledReason(shipment, user)
    if (blocked) {
      Alert.error(blocked)
      return
    }
    const confirmed = await Alert.confirm(
      `Revert delivery for ${shipment.shipment_id}? It will move back to Received, payment will become To-Pay, and collected amount will be reversed.`,
      "Revert Delivery",
    )
    if (!confirmed) return
    try {
      setRevertingId(shipment.shipment_id)
      await shipmentService.revertDelivery(shipment.shipment_id)
      Alert.success("Delivery reverted to Received")
      await fetchData()
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to revert delivery")
    } finally {
      setRevertingId("")
    }
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"]
    if (!allowedTypes.includes(file.type)) {
      Alert.error("Invalid file type. Only JPG, PNG, GIF, and WebP are allowed.")
      return
    }
    if (file.size > 2097152) {
      Alert.error("File size too large. Maximum size is 2MB.")
      return
    }
    setDeliveryReceiverImage(file)
    const reader = new FileReader()
    reader.onloadend = () => setImagePreview(reader.result)
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setDeliveryReceiverImage(null)
    setImagePreview(null)
    const fileInput = document.getElementById("agent-delivery-receiver-image")
    if (fileInput) fileInput.value = ""
  }

  const resetForm = () => {
    setSelectedById({})
    setDeliveryReceiver("")
    setDeliveryReceiverContact("")
    setDeliveryReceiverImage(null)
    setImagePreview(null)
    setDeliveryPhoto(null)
    setDeliveryPhotoPreview(null)
    setAgentUnloadingAmount("")
    setDeliveryVehicleNo("")
    setLastMileHours("2")
    setPaymentAmount("")
    setPendingDeliverAfterPay(null)
  }

  const runStatusUpdate = async (shipmentIds) => {
    const response = await shipmentService.bulkUpdateStatus(
      shipmentIds,
      selectedStatus,
      remarks,
      deliveryReceiver,
      deliveryReceiverImage,
      deliveryReceiverContact,
      agentUnloadingAmount,
      deliveryPhoto,
      deliveryVehicleNo,
      lastMileHours,
    )
    if (isOtpRequiredResponse(response.data)) {
      setOtpPending(normalizeOtpPending(response.data))
      if (response.data.updated_count > 0) {
        Alert.success(
          `Updated ${response.data.updated_count} shipment(s). Verify OTP to deliver the rest.`,
        )
      } else {
        Alert.success(response.data.message || "OTP sent on WhatsApp")
      }
      return
    }
    if (response.data?.success) {
      const failed = response.data.failed_shipments || []
      if (failed.length > 0 && response.data.updated_count === 0) {
        Alert.error(failed[0]?.reason || "Failed to update shipments")
      } else {
        const ewayNote = (response.data.eway_bill_warnings || []).filter(Boolean).join(" ")
        Alert.success(
          ewayNote
            ? `Updated ${response.data.updated_count} shipment(s) to ${selectedStatus}. ${ewayNote}`
            : `Successfully updated ${response.data.updated_count} shipment(s) to ${selectedStatus}`,
        )
      }
      resetForm()
      await fetchData()
    } else {
      Alert.error(response.data?.error || "Failed to update shipments")
    }
  }

  const collectDeliveryPayment = async (shipment) => {
    const balance = parseFloat(shipment.balance_amount || 0)
    const amount = parseFloat(paymentAmount || 0)
    if (Number.isNaN(amount) || amount <= 0) {
      throw new Error("Enter a valid payment amount")
    }
    if (amount > balance + 0.001) {
      throw new Error(`Payment cannot exceed balance (₹${balance.toFixed(2)})`)
    }
    if (amount + 0.001 < balance) {
      throw new Error(`Pay the full balance (₹${balance.toFixed(2)}) to mark as Delivered`)
    }

    const payload = new FormData()
    payload.append("payment_amount", String(amount))
    payload.append("payment_mode", paymentMode)
    payload.append("remarks", remarks || "COD collected on delivery")

    const response = await paymentService.addPayment(shipment.shipment_id, payload)
    if (response.data?.requires_online_payment && response.data?.payment_intent) {
      setPaymentIntent(response.data.payment_intent)
      setPendingDeliverAfterPay([shipment.shipment_id])
      setShowPaymentQr(true)
      return { deferred: true }
    }
    return { deferred: false }
  }

  const handleBulkStatusUpdate = async () => {
    let shipmentIds = selectedList.map((s) => s.shipment_id)
    if (selectedStatus === "Delivered") {
      shipmentIds = deliverableSelected.map((s) => s.shipment_id)
      if (shipmentIds.length === 0) {
        Alert.error("Settle that ML sheet before you can deliver its shipments")
        return
      }
    }
    if (shipmentIds.length === 0) {
      Alert.error("Please select at least one shipment that is not already Delivered")
      return
    }
    if (!selectedStatus) {
      Alert.error("Please select a status")
      return
    }
    if (selectedStatus === "Delivered" && !allowDelivery) {
      Alert.error("Delivery is not allowed for your assigned hub")
      return
    }
    if (!statusOptions.some((o) => o.value === selectedStatus)) {
      Alert.error("Selected status is not allowed for the chosen shipments")
      return
    }
    if (selectedStatus === "Delivered" && unpaidSelected.length > 1) {
      Alert.error(
        "Multiple unpaid shipments selected. Select one at a time to collect delivery payment.",
      )
      return
    }
    if (selectedStatus === "Delivered") {
      if (!deliveryReceiver.trim()) {
        Alert.error("Receiver name is required")
        return
      }
      if (!deliveryReceiverContact.trim()) {
        Alert.error("Receiver contact number is required")
        return
      }
      if (!deliveryReceiverImage) {
        Alert.error("Government ID proof image is required")
        return
      }
      if (!deliveryPhoto) {
        Alert.error("Signed GC receipt is required")
        return
      }
    }

    try {
      setUpdating(true)
      if (needsDeliveryPayment && unpaidSelected.length === 1) {
        const result = await collectDeliveryPayment(unpaidSelected[0])
        if (result.deferred) return
      }
      await runStatusUpdate(shipmentIds)
    } catch (error) {
      const message = error.response?.data?.error || error.message || "Failed to update shipments"
      if (message !== "Payment cancelled") Alert.error(message)
    } finally {
      setUpdating(false)
    }
  }

  const handleOnlinePaymentSuccess = async () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    const ids = pendingDeliverAfterPay
    setPendingDeliverAfterPay(null)
    try {
      setUpdating(true)
      if (ids?.length) {
        await runStatusUpdate(ids)
      } else {
        Alert.success("Payment received")
        await fetchData()
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || "Payment received but status update failed")
      await fetchData()
    } finally {
      setUpdating(false)
    }
  }

  const handleOnlinePaymentCancel = () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    setPendingDeliverAfterPay(null)
    setUpdating(false)
  }

  const getShipmentStatusColor = (status) => {
    const statusMap = {
      booked: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      received: "bg-yellow-100 text-yellow-800",
      delivered: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      returned: "bg-orange-100 text-orange-800",
    }
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  const hideCustomerDetails = user?.role === "Agent"
  const detailsVisible = (shipment) => {
    if (!hideCustomerDetails) return true
    if (typeof shipment?.customer_details_visible === "boolean") {
      return shipment.customer_details_visible
    }
    const mode = String(shipment?.mode_of_payment || "").toLowerCase()
    if (mode === "paid" || mode === "paid_balance" || mode === "credit") return true
    if (mode === "to_pay") {
      if (!shipment?.delivery_locked_until_settlement) return true
      return parseFloat(shipment?.balance_amount || 0) <= 0
    }
    return true
  }
  const amountsVisible = (shipment) => {
    if (!hideCustomerDetails) return true
    if (typeof shipment?.amounts_visible === "boolean") {
      return shipment.amounts_visible
    }
    const mode = String(shipment?.mode_of_payment || "").toLowerCase()
    // To-Pay amounts stay hidden until Settle-with-Company clears the ML.
    if (mode === "to_pay" && shipment?.delivery_locked_until_settlement) return false
    return (
      shipment?.total_amount != null ||
      shipment?.balance_amount != null ||
      detailsVisible(shipment)
    )
  }

  if (loading && shipments.length === 0 && !searchQuery.trim()) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading Update Status..." />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className={isMobileLayout ? "pb-36 min-h-0" : ""}>
        {isMobileLayout ? (
          <div className="sticky top-0 z-30 bg-white shrink-0 mb-2 pb-2 border-b border-gray-100">
            <h1 className="flex items-center gap-1.5 text-sm font-bold text-secondary truncate min-w-0">
              <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
              <span className="truncate">{t("sidebar.updateStatus", "Update Status")}</span>
            </h1>
            <p className="text-[10px] text-gray-500 mt-1">
              ML sheet shipments assigned to you
            </p>
          </div>
        ) : (
          <div className="p-4 sm:p-6 sm:px-0 mb-4 sm:py-2 sm:mb-2">
            <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
              <Package className="w-6 h-6 text-buttonhoverTxt" />
              {t("sidebar.updateStatus", "Update Status")}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {user?.role === "Agent"
                ? "Shipments from Master Loading Sheets assigned to you only."
                : "Shipments linked to Master Loading Sheets you can manage."}
            </p>
          </div>
        )}

        {!allowDelivery && user?.role === "Agent" && (
          <div className="mb-3 sm:mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs sm:text-sm">
            Delivery is not enabled for your location hub. You can mark shipments as Received,
            but Delivered is disabled.
          </div>
        )}

        <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-6 shadow-sm md:shadow-none">
          <h3 className="hidden md:block text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">
            Update Status
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Select Status <span className="text-red-500">*</span>
              </label>
              <SelectField
                variant="form"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                options={statusOptions}
                className="min-w-0 w-full"
              />
              <p className="text-[9px] sm:text-xs text-gray-500 mt-1 leading-snug">
                Dispatched → Received. Received → Delivered after your shipment settlement
                {canDeliver ? "" : allowDelivery ? " (settlement pending)" : " (hub delivery off)"}.
              </p>
              {selectedList.length > 0 && bulkStatusConfig.paymentMessage && (
                <p className="text-[9px] sm:text-xs text-amber-700 mt-1">{bulkStatusConfig.paymentMessage}</p>
              )}
              {lockedSelected.length > 0 && deliverableSelected.length > 0 && (
                <p className="text-[9px] sm:text-xs text-amber-700 mt-1">
                  Delivered applies only to shipments whose ML sheet is already settled.
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Selected
              </label>
              <div className="flex items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border rounded-xl bg-blue-50/50 min-h-[38px] sm:min-h-[42px]">
                <div className="text-xs sm:text-sm font-bold text-secondary">{selectedList.length}</div>
                <div className="text-[10px] sm:text-sm text-secondary font-medium ml-1 truncate">
                  / {selectableFilteredShipments.length} on page
                </div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1 min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional remarks..."
                className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 resize-none md:border-2"
                rows={isMobileLayout ? 2 : 1}
              />
            </div>
          </div>

          {selectedStatus === "Delivered" && canDeliver && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 p-2 sm:p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="col-span-2 md:col-span-3">
                <h4 className="text-[10px] sm:text-sm font-semibold text-orange-800">
                  Delivery Information
                </h4>
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Last-mile vehicle
                </label>
                <input
                  type="text"
                  value={deliveryVehicleNo}
                  onChange={(e) => setDeliveryVehicleNo(e.target.value.toUpperCase())}
                  placeholder="Needed when an e-Way Bill is on the GC"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  maxLength={20}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  E-Way close window
                </label>
                <select
                  value={lastMileHours}
                  onChange={(e) => setLastMileHours(e.target.value)}
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                >
                  <option value="1">1 hour</option>
                  <option value="2">2 hours</option>
                  <option value="3">3 hours</option>
                </select>
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Receiver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={deliveryReceiver}
                  onChange={(e) => setDeliveryReceiver(e.target.value)}
                  placeholder="Receiver name"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  maxLength={250}
                  required
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Receiver Contact <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={deliveryReceiverContact}
                  onChange={(e) =>
                    setDeliveryReceiverContact(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  placeholder="10-digit contact number"
                  inputMode="numeric"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  maxLength={10}
                  required
                />
              </div>
              <DeliveryProofCapture
                label="Government ID proof"
                required
                file={deliveryReceiverImage}
                preview={imagePreview}
                inputId="agent-delivery-receiver-image"
                onError={(msg) => Alert.error(msg)}
                onFile={(next) => {
                  if (!next) {
                    setDeliveryReceiverImage(null)
                    setImagePreview(null)
                    return
                  }
                  setDeliveryReceiverImage(next)
                  const reader = new FileReader()
                  reader.onloadend = () => setImagePreview(reader.result)
                  reader.readAsDataURL(next)
                }}
              />
              <DeliveryProofCapture
                label="Signed GC receipt"
                hint="Upload the printed receipt PDF, or a photo of the signed short receipt. GC number must be visible."
                required
                allowPdf
                file={deliveryPhoto}
                preview={deliveryPhotoPreview}
                inputId="agent-delivery-photo"
                onError={(msg) => Alert.error(msg)}
                onFile={(next) => {
                  if (!next) {
                    setDeliveryPhoto(null)
                    setDeliveryPhotoPreview(null)
                    return
                  }
                  setDeliveryPhoto(next)
                  const reader = new FileReader()
                  reader.onloadend = () => setDeliveryPhotoPreview(reader.result)
                  reader.readAsDataURL(next)
                }}
              />
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Unloading amount (agent only)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={agentUnloadingAmount}
                  onChange={(e) => setAgentUnloadingAmount(e.target.value)}
                  placeholder="Not added to GC or ML totals"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                />
              </div>
            </div>
          )}

          {needsDeliveryPayment && canDeliver && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h4 className="text-xs sm:text-sm font-semibold text-amber-900 mb-2">
                Collect payment on delivery
              </h4>
              {unpaidSelected.length > 1 ? (
                <p className="text-xs sm:text-sm text-red-600">
                  Select only one unpaid shipment to collect Cash or UPI, then mark Delivered.
                </p>
              ) : (
                <>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    <span className="font-semibold">{unpaidSelected[0].shipment_id}</span> balance{" "}
                    <span className="font-semibold">
                      ₹{parseFloat(unpaidSelected[0].balance_amount || 0).toFixed(2)}
                    </span>
                    . Pay full balance to mark Delivered &amp; Paid.
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
                        To-Pay Amount (locked)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentAmount}
                        readOnly
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
                        Method
                      </label>
                      <select
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-xl"
                      >
                        <option value="upi">UPI (QR)</option>
                        <option value="cash">Cash</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="hidden md:flex items-end gap-2">
          <Button
            variant="primary"
            onClick={handleBulkStatusUpdate}
            disabled={
              updating ||
              selectedList.length === 0 ||
              (needsDeliveryPayment && unpaidSelected.length > 1)
            }
            loading={updating}
          >
            {updating
              ? "Updating..."
              : needsDeliveryPayment
                ? `Pay & Update ${selectedList.length} Shipment(s)`
                : `Update ${selectedList.length} Shipment(s)`}
          </Button>
          {statusFilter === "Dispatched" && (
            <>
          <div className="min-w-[260px] flex-1 max-w-md">
            <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
              Missing reason <span className="text-red-500">*</span>
            </label>
            <select
              value={missingNote}
              onChange={(e) => setMissingNote(e.target.value)}
              className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2 bg-white"
            >
              <option value="">Select a reason</option>
              {MISSING_NOTE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="secondary"
            onClick={handleMarkMissing}
            disabled={updating || selectedList.length === 0}
          >
            Mark missing
          </Button>
            </>
          )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden max-w-full">
          <div className="px-3 sm:px-6 py-2 sm:py-4 border-b border-gray-200 space-y-2 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAll}
                  disabled={selectableFilteredShipments.length === 0}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-gray-400 cursor-pointer accent-blue-500 disabled:opacity-40"
                />
                <h3 className="text-xs sm:text-lg font-semibold text-gray-700 mb-0">
                  Shipments ({filteredShipments.length}
                  {totalCount > filteredShipments.length ? ` / ${totalCount}` : ""}
                  )
                </h3>
              </div>
              <div className="flex-1 min-w-0">
                <SearchSubmitInput
                  value={searchQuery}
                  onSearch={setSearchQuery}
                  placeholder={
                    hideCustomerDetails
                      ? "Search by shipment ID or ML sheet..."
                      : "Search by ID, name, contact, ML sheet..."
                  }
                  inputClassName="w-full py-1.5 sm:py-2.5 border border-gray-300 rounded-xl text-[11px] sm:text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-orange-400"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-[10px] sm:text-xs font-semibold text-gray-600 mr-0.5">
                Status:
              </span>
              {[...FILTER_OPTIONS, "Missing"].map((status) => {
                const active = statusFilter === status
                const missingChip = status === "Missing"
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => selectStatusFilter(status)}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-xl border transition-colors ${
                      active
                        ? missingChip
                          ? "bg-red-600 text-white border-red-600"
                          : "bg-secondary text-white border-secondary"
                        : missingChip
                          ? "bg-white text-red-700 border-red-200 hover:border-red-400"
                          : "bg-white text-gray-600 border-gray-300 hover:border-secondary"
                    }`}
                  >
                    {status}
                  </button>
                )
              })}
              <span className="hidden sm:inline text-xs text-gray-500 ml-1">
                Showing {statusFilter === "Missing" ? "missing" : statusFilter} only
              </span>
            </div>
          </div>

          {loading && shipments.length > 0 && (
            <div className="px-3 sm:px-6 py-2 text-[11px] sm:text-xs text-gray-500 border-b border-gray-100">
              Loading latest shipments...
            </div>
          )}

          {remitLockedSheetCount > 0 && user?.role === "Agent" && (
            <div className="mx-3 sm:mx-6 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              You can mark every shipment Received. Delivered opens only for an ML sheet whose To-Pay
              settlement is already completed. Other sheets on this page stay on Received.
              {remitLockedSheetCount === 1
                ? " One ML sheet is waiting on that settlement."
                : ` ${remitLockedSheetCount} ML sheets are waiting on that settlement.`}
            </div>
          )}

          {filteredShipments.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-gray-500 text-sm sm:text-lg">
                {loading
                  ? "Looking up that shipment..."
                  : totalCount === 0 && !searchQuery.trim()
                    ? statusFilter === "Missing"
                      ? "No missing shipments on ML sheets you can update"
                      : `No ${statusFilter.toLowerCase()} shipments on ML sheets assigned to you`
                    : "No shipments match the selected filters"}
              </p>
            </div>
          ) : (
            <>
              <div className="hidden xl:block w-full overflow-x-auto">
                <table className="w-full min-w-[960px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={handleSelectAll}
                          disabled={selectableFilteredShipments.length === 0}
                          className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Shipment ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        ML Sheet
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        {hideCustomerDetails ? "From / Sender" : "Sender"}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        {hideCustomerDetails ? "To / Receiver" : "Receiver"}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Parcel / Package
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Amount / Balance
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredShipments.map((shipment, index) => {
                      const delivered = isStatusUpdateLocked(shipment.status)
                      const locked = delivered
                      return (
                        <tr
                          key={shipment.id || shipment.shipment_id}
                          className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${
                            locked ? "opacity-60" : "hover:bg-gray-50"
                          } ${
                            selectedShipments.has(shipment.shipment_id)
                              ? "ring-1 ring-inset ring-orange-300"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedShipments.has(shipment.shipment_id)}
                              onChange={() =>
                                handleSelectShipment(shipment.shipment_id, shipment.status, shipment)
                              }
                              disabled={locked}
                              title={locked ? "Already delivered — locked" : undefined}
                              className="w-4 h-4 rounded border border-gray-400 accent-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            {detailsVisible(shipment) ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                className="font-semibold text-secondary hover:text-orange-600 hover:underline"
                              >
                                {shipment.shipment_id}
                              </button>
                            ) : (
                              <span className="font-semibold text-gray-900">{shipment.shipment_id}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {shipment.ml_sheet_id || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            <div>{shipment.sender_location || "—"}</div>
                            {detailsVisible(shipment) && (
                              <>
                                <div className="font-medium text-gray-900">{shipment.sender_name || "—"}</div>
                                <div className="text-xs text-gray-500">{shipment.sender_contact}</div>
                                {shipment.sender_address && (
                                  <div className="text-xs text-gray-500">{shipment.sender_address}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            <div>{shipment.receiver_location || "—"}</div>
                            {detailsVisible(shipment) && (
                              <>
                                <div className="font-medium text-gray-900">{shipment.receiver_name || "—"}</div>
                                <div className="text-xs text-gray-500">{shipment.receiver_contact}</div>
                                {shipment.receiver_address && (
                                  <div className="text-xs text-gray-500">{shipment.receiver_address}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {packageLines(shipment).length ? (
                              packageLines(shipment).map((line, lineIndex) => (
                                <div key={`${shipment.shipment_id}-pkg-${lineIndex}`} className="break-words">
                                  {line.name || "—"}
                                </div>
                              ))
                            ) : (
                              "—"
                            )}
                            {shipment.parcel_detail && (
                              <div className="text-xs text-gray-500 mt-0.5 break-words">
                                {shipment.parcel_detail}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-800">
                            {packageLines(shipment).length ? (
                              packageLines(shipment).map((line, lineIndex) => (
                                <div key={`${shipment.shipment_id}-qty-${lineIndex}`}>
                                  {line.qty ?? "—"}
                                </div>
                              ))
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            {amountsVisible(shipment) ? (
                              <>
                                <div className="font-semibold">
                                  ₹{parseFloat(shipment.total_amount || 0).toFixed(2)}
                                </div>
                                <div
                                  className={
                                    parseFloat(shipment.balance_amount || 0) > 0
                                      ? "text-xs text-amber-700 font-semibold"
                                      : "text-xs text-green-700"
                                  }
                                >
                                  Bal: ₹{parseFloat(shipment.balance_amount || 0).toFixed(2)}
                                </div>
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${getShipmentStatusColor(
                                shipment.status,
                              )}`}
                            >
                              {shipment.status}
                            </span>
                            <HubShortfallNote shipment={shipment} />
                            <div className="text-xs text-gray-500 mt-1">
                              {shipment.mode_of_payment}
                            </div>
                            {shipment.delivery_locked_until_settlement && (
                              <div className="text-xs text-amber-700 font-semibold mt-1">
                                {String(shipment.mode_of_payment || "").toLowerCase() === "to_pay"
                                  ? `Settle ${shipment.ml_sheet_id || "this ML sheet"} before receiving parcel`
                                  : `Settle ${shipment.ml_sheet_id || "this ML sheet"} before delivery`}
                              </div>
                            )}
                            {delivered && (
                              <div className="text-xs text-red-500 font-semibold mt-0.5">Locked</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              {delivered && (
                                <button
                                  type="button"
                                  onClick={() => handleRevertDelivery(shipment)}
                                  disabled={!canRevertDelivery(shipment, user) || revertingId === shipment.shipment_id}
                                  title={
                                    canRevertDelivery(shipment, user)
                                      ? "Revert delivery to Received"
                                      : revertDeliveryDisabledReason(shipment, user)
                                  }
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Undo2 className={`w-4 h-4 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                </button>
                              )}
                              {detailsVisible(shipment) && (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                  className="text-orange-600 hover:text-orange-700 font-semibold text-sm hover:underline"
                                >
                                  View →
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-2 p-2 sm:p-4">
                {filteredShipments.map((shipment) => {
                  const isSelected = selectedShipments.has(shipment.shipment_id)
                  const delivered = isStatusUpdateLocked(shipment.status)
                  const locked = delivered
                  return (
                    <div
                      key={shipment.id || shipment.shipment_id}
                      className={`bg-white rounded-xl shadow-sm border p-2.5 sm:p-4 border-l-4 transition-colors ${
                        locked
                          ? "opacity-60 border-gray-200 border-l-green-500"
                          : isSelected
                            ? "border-orange-400 border-l-orange-500 bg-orange-50/40"
                            : "border-gray-100 border-l-orange-500"
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            handleSelectShipment(shipment.shipment_id, shipment.status, shipment)
                          }
                          disabled={locked}
                          className="w-4 h-4 rounded border-2 border-gray-400 accent-blue-500 mt-0.5 shrink-0 disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-1">
                            {detailsVisible(shipment) ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                className="text-orange-600 font-bold text-xs sm:text-sm break-all hover:underline text-left"
                              >
                                {shipment.shipment_id}
                              </button>
                            ) : (
                              <span className="text-gray-900 font-bold text-xs sm:text-sm break-all text-left">
                                {shipment.shipment_id}
                              </span>
                            )}
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase shrink-0 ${getShipmentStatusColor(
                                shipment.status,
                              )}`}
                            >
                              {shipment.status}
                            </span>
                          </div>
                          <HubShortfallNote shipment={shipment} />
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            ML: {shipment.ml_sheet_id || "—"}
                          </p>
                          {shipment.delivery_locked_until_settlement && (
                            <p className="text-[10px] text-amber-700 font-semibold mt-1">
                              {String(shipment.mode_of_payment || "").toLowerCase() === "to_pay"
                                ? `Settle ${shipment.ml_sheet_id || "this ML sheet"} before receiving parcel`
                                : `Settle ${shipment.ml_sheet_id || "this ML sheet"} before delivery`}
                            </p>
                          )}
                          {amountsVisible(shipment) && (
                            <p className="text-xs sm:text-sm font-bold text-gray-900 mt-1">
                              ₹{parseFloat(shipment.total_amount || 0).toFixed(2)}
                              {parseFloat(shipment.balance_amount || 0) > 0 && (
                                <span className="ml-2 text-amber-700 font-semibold">
                                  Bal ₹{parseFloat(shipment.balance_amount || 0).toFixed(2)}
                                </span>
                              )}
                            </p>
                          )}
                          {delivered && (
                            <p className="text-[10px] text-red-500 font-semibold mt-1">
                              Already delivered — locked
                            </p>
                          )}
                          {delivered && (
                            <button
                              type="button"
                              onClick={() => handleRevertDelivery(shipment)}
                              disabled={!canRevertDelivery(shipment, user) || revertingId === shipment.shipment_id}
                              title={
                                canRevertDelivery(shipment, user)
                                  ? "Revert delivery to Received"
                                  : revertDeliveryDisabledReason(shipment, user)
                              }
                              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 disabled:opacity-40"
                            >
                              <Undo2 className={`w-3.5 h-3.5 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                              Revert delivery
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-sm">
                        <div className="min-w-0">
                          <p className="text-gray-500 font-medium">From / Sender</p>
                          <p className="font-semibold text-gray-800 break-words leading-tight">
                            {shipment.sender_location || "—"}
                          </p>
                          {detailsVisible(shipment) && (
                            <>
                              <p className="text-gray-800 break-words">{shipment.sender_name || "—"}</p>
                              <p className="text-gray-500">{shipment.sender_contact}</p>
                            </>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-500 font-medium">To / Receiver</p>
                          <p className="font-semibold text-gray-800 break-words leading-tight">
                            {shipment.receiver_location || "—"}
                          </p>
                          {detailsVisible(shipment) && (
                            <>
                              <p className="text-gray-800 break-words">{shipment.receiver_name || "—"}</p>
                              <p className="text-gray-500">{shipment.receiver_contact}</p>
                            </>
                          )}
                        </div>
                        <div className="min-w-0 col-span-2">
                          <p className="text-gray-500 font-medium">Parcel / Qty</p>
                          {packageLines(shipment).length ? (
                            packageLines(shipment).map((line, lineIndex) => (
                              <p
                                key={`${shipment.shipment_id}-m-${lineIndex}`}
                                className="font-semibold text-gray-800 break-words"
                              >
                                {line.name || "—"} · {line.qty ?? "—"}
                              </p>
                            ))
                          ) : (
                            <p className="font-semibold text-gray-800">—</p>
                          )}
                          {shipment.parcel_detail && (
                            <p className="text-gray-500 break-words">{shipment.parcel_detail}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
          {totalCount > 0 && (
            <div className={isMobileLayout ? "pb-2" : ""}>
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                loading={loading}
                onNext={() => setCurrentPage((page) => page + 1)}
              />
            </div>
          )}
        </div>
      </div>

      {isMobileLayout && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
          {statusFilter === "Dispatched" && (
            <>
          <label className="block text-[10px] font-semibold text-gray-700 mb-1">
            Missing reason <span className="text-red-500">*</span>
          </label>
          <select
            value={missingNote}
            onChange={(e) => setMissingNote(e.target.value)}
            className="w-full mb-2 px-2 py-1.5 text-[11px] border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 bg-white"
          >
            <option value="">Select a reason</option>
            {MISSING_NOTE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
            </>
          )}
          <div className={statusFilter === "Dispatched" ? "grid grid-cols-2 gap-2" : ""}>
          <Button
            variant="primary"
            type="button"
            icon={RefreshCw}
            onClick={handleBulkStatusUpdate}
            disabled={
              updating ||
              selectedList.length === 0 ||
              (needsDeliveryPayment && unpaidSelected.length > 1)
            }
            loading={updating}
            fullWidth
            className="w-full justify-center"
          >
            {updating
              ? "Updating..."
              : needsDeliveryPayment
                ? `Pay & Update ${selectedList.length}`
                : `Update ${selectedList.length} Shipment(s)`}
          </Button>
          {statusFilter === "Dispatched" && (
          <Button
            variant="secondary"
            type="button"
            onClick={handleMarkMissing}
            disabled={updating || selectedList.length === 0}
            fullWidth
            className="w-full justify-center"
          >
            Mark missing
          </Button>
          )}
          </div>
        </div>
      )}

      <PaymentQrModal
        open={showPaymentQr}
        intent={paymentIntent}
        title="Scan UPI QR to Pay"
        onSuccess={handleOnlinePaymentSuccess}
        onCancel={handleOnlinePaymentCancel}
      />
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={async () => {
          setOtpPending([])
          resetForm()
          Alert.success("Delivery verified and completed")
          await fetchData()
        }}
      />
    </DashboardLayout>
  )
}

export default AgentUpdateStatus
